// API client — replaces window.storage with Azure Functions + Cosmos DB
import { authHeaders } from "../auth/apiToken.js";

const BASE = "/api";

async function request(path, options = {}) {
  const headers = await authHeaders({ "Content-Type": "application/json", ...options.headers });
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || `API error ${res.status}`);
  }
  return res.json();
}

// Like request(), but never throws — returns { ok, status, data } so the board can
// tell a 409 concurrency conflict apart from a 404/422/500 (or a network drop,
// status 0) and always revert its optimistic move + show a specific message.
async function writeRequest(path, options = {}) {
  try {
    const headers = await authHeaders({ "Content-Type": "application/json", ...options.headers });
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      headers,
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, data };
  } catch {
    // fetch rejects on network failure / DNS / CORS — surface it as a result, not
    // a throw, so the caller's rollback path still runs.
    return { ok: false, status: 0, data: { error: "Network error — check your connection and try again." } };
  }
}

// Build a query string from a params object, skipping null/empty values.
function qs(params) {
  const entries = Object.entries(params || {}).filter(([, v]) => v != null && v !== "");
  if (!entries.length) return "";
  return "?" + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
}

// Every item of a paged list endpoint ({ items, continuationToken }), following continuation
// tokens. Returns null if any page fails, so callers never show a silently partial list.
async function listAll(path, params = {}) {
  const items = [];
  let continuation;
  try {
    do {
      const res = await request(`${path}${qs({ ...params, limit: 200, continuation })}`);
      items.push(...(res?.items || []));
      continuation = res?.continuationToken || undefined;
    } while (continuation);
    return items;
  } catch {
    return null;
  }
}

// ─── Generic CRUD ───
export async function listItems(collection) {
  return request(`/${collection}`);
}

export async function upsertItem(collection, item) {
  return request(`/${collection}/item`, {
    method: "PUT",
    body: JSON.stringify(item),
  });
}

export async function deleteItem(collection, id) {
  return request(`/${collection}/${id}`, { method: "DELETE" });
}

export async function bulkUpsert(collection, items) {
  return request(`/${collection}/bulk`, {
    method: "PUT",
    body: JSON.stringify(items),
  });
}

// ─── Goals (key-value style) ───
export async function fetchGoals() {
  return request("/goals");
}

export async function saveGoal(goalKey, projectId, data) {
  return request("/goals/item", {
    method: "PUT",
    body: JSON.stringify({ id: goalKey, projectId, ...data }),
  });
}

// ─── Convenience wrappers matching old API ───
export const api = {
  proposals: {
    list: () => listItems("proposals"),
    save: (item) => upsertItem("proposals", item),
    delete: (id) => deleteItem("proposals", id),
    bulkSave: (items) => bulkUpsert("proposals", items),
    // Guarded writes (increment 2). Both send the _etag the client last read;
    // a 409 in the result means the record changed underneath — refetch + revert.
    setStage: (id, toStage, expectedEtag, changedBy) =>
      writeRequest(`/proposals/${id}/stage`, {
        method: "POST",
        body: JSON.stringify({ toStage, expectedEtag, changedBy }),
      }),
    updateFields: (id, fields, expectedEtag) =>
      writeRequest(`/proposals/${id}/fields`, {
        method: "PUT",
        body: JSON.stringify({ ...fields, expectedEtag }),
      }),
  },
  archived: {
    list: () => listItems("archived"),
    save: (item) => upsertItem("archived", item),
    delete: (id) => deleteItem("archived", id),
  },
  events: {
    list: () => listItems("events"),
    save: (item) => upsertItem("events", item),
    delete: (id) => deleteItem("events", id),
  },
  discussions: {
    list: () => listItems("discussions"),
    save: (item) => upsertItem("discussions", item),
    delete: (id) => deleteItem("discussions", id),
  },
  // Client registry (v2). Thin {id, name} records. Clients are created only here (via
  // create, with server-side unique-name enforcement) — never deleted. create() uses
  // writeRequest so the caller can tell a 409 "already exists" apart and select instead.
  // Account Owners dashboard: every rollup in one read (period = 12m | 6m | ytd | all).
  dashboard: { get: (period = "12m") => request(`/dashboard?period=${encodeURIComponent(period)}`) },
  clients: {
    list: () => request("/clients"),
    get: (id) => request(`/clients/${id}`),
    create: (name, logoUrl) => writeRequest("/clients", { method: "POST", body: JSON.stringify({ name, logoUrl }) }),
    // Edit detail fields (description, sharepointUrl, pocs) — never name.
    update: (id, fields) => writeRequest(`/clients/${id}`, { method: "PUT", body: JSON.stringify(fields) }),
    // Aggregated per-client rollups for the Clients page cards.
    summary: () => request("/client-summary"),
    // Running-notes feed: append-only; editable, never deletable.
    notes: {
      list: (clientId) => request(`/client-notes${qs({ clientId })}`),
      create: (clientId, text) => writeRequest("/client-notes", { method: "POST", body: JSON.stringify({ clientId, text }) }),
      update: (id, text) => writeRequest(`/client-notes/${id}`, { method: "PUT", body: JSON.stringify({ text }) }),
    },
    // Contract PDF slots in Blob (increment 8). slot = "contract" | "redacted".
    file: {
      upload: async (clientId, slot, file) => {
        const headers = await authHeaders({ "Content-Type": "application/pdf" });
        const res = await fetch(`${BASE}/client-files/${clientId}/${slot}`, { method: "POST", body: file, headers });
        const data = await res.json().catch(() => ({}));
        return { ok: res.ok, status: res.status, data };
      },
      sas: (clientId, slot) => request(`/client-files/${clientId}/${slot}/sas`), // { url } — 15-min read link
    },
    // A client's contracts (up to 5), each stamped with when/who added it.
    contracts: {
      upload: async (clientId, file) => {
        const headers = await authHeaders({ "Content-Type": "application/pdf" });
        const res = await fetch(`${BASE}/client-contracts/${clientId}?name=${encodeURIComponent(file.name || "")}`, { method: "POST", body: file, headers });
        const data = await res.json().catch(() => ({}));
        return { ok: res.ok, status: res.status, data };
      },
      sas: (clientId, contractId) => request(`/client-contracts/${clientId}/${contractId}/sas`), // { url } — 15-min read link
      remove: (clientId, contractId) => writeRequest(`/client-contracts/${clientId}/${contractId}`, { method: "DELETE" }),
    },
  },
  // Shared work model (v2, increment 4). Both list endpoints return
  // { items, continuationToken }. Filter tasks by {clientId} (Clients page) or
  // {userId} (Employees page); objectives by {clientId}. update() may include a
  // `status` change — the server appends the history entry.
  objectives: {
    list: (params = {}) => request(`/objectives${qs(params)}`),
    listAll: (params = {}) => listAll("/objectives", params),
    get: (id) => request(`/objectives/${id}`),
    create: (obj) => writeRequest("/objectives", { method: "POST", body: JSON.stringify(obj) }),
    update: (id, fields) => writeRequest(`/objectives/${id}`, { method: "PUT", body: JSON.stringify(fields) }),
    remove: (id) => writeRequest(`/objectives/${id}`, { method: "DELETE" }),
  },
  tasks: {
    list: (params = {}) => request(`/tasks${qs(params)}`),
    listAll: (params = {}) => listAll("/tasks", params),
    get: (id) => request(`/tasks/${id}`),
    create: (task) => writeRequest("/tasks", { method: "POST", body: JSON.stringify(task) }),
    update: (id, fields) => writeRequest(`/tasks/${id}`, { method: "PUT", body: JSON.stringify(fields) }),
    remove: (id) => writeRequest(`/tasks/${id}`, { method: "DELETE" }),
  },
  // CRM (spec 2026-11-03). Writes return { ok, status, data } so a 409's existingId (duplicate
  // email / contact already on the client) reaches the caller; lists follow every page.
  contacts: {
    listAll: () => listAll("/contacts"),
    get: (id) => request(`/contacts/${id}`),
    create: (c) => writeRequest("/contacts", { method: "POST", body: JSON.stringify(c) }),
    update: (id, fields) => writeRequest(`/contacts/${id}`, { method: "PUT", body: JSON.stringify(fields) }),
    remove: (id) => writeRequest(`/contacts/${id}`, { method: "DELETE" }),
  },
  crmTracks: {
    listAll: (params = {}) => listAll("/crm-tracks", params),
    get: (id) => request(`/crm-tracks/${id}`),
    create: (t) => writeRequest("/crm-tracks", { method: "POST", body: JSON.stringify(t) }),
    update: (id, fields) => writeRequest(`/crm-tracks/${id}`, { method: "PUT", body: JSON.stringify(fields) }),
    remove: (id) => writeRequest(`/crm-tracks/${id}`, { method: "DELETE" }),
    addActivity: (id, text) => writeRequest(`/crm-tracks/${id}/activity`, { method: "POST", body: JSON.stringify({ text }) }),
    editActivity: (id, entryId, text) => writeRequest(`/crm-tracks/${id}/activity/${entryId}`, { method: "PUT", body: JSON.stringify({ text }) }),
    removeActivity: (id, entryId) => writeRequest(`/crm-tracks/${id}/activity/${entryId}`, { method: "DELETE" }),
  },
  // Task templates (v2, increment 7). A template = one objective + its tasks. import() stamps
  // a new objective + tasks (independent copies) onto a client project or an internal objective.
  templates: {
    list: () => request("/templates"),
    get: (id) => request(`/templates/${id}`),
    create: (tmpl) => writeRequest("/templates", { method: "POST", body: JSON.stringify(tmpl) }),
    update: (id, tmpl) => writeRequest(`/templates/${id}`, { method: "PUT", body: JSON.stringify(tmpl) }),
    remove: (id) => writeRequest(`/templates/${id}`, { method: "DELETE" }),
    import: (id, { clientId, projectId, startDate, includeOptional }) => writeRequest(`/templates/${id}/import`, { method: "POST", body: JSON.stringify({ clientId, projectId, startDate, includeOptional }) }),
  },
  // Update Meetings (spec 2026-09-29). list/get throw on error; writes return { ok, status, data }.
  // A stale section/block save returns status 409 with data.current = the server's copy.
  meetings: {
    list: (clientId) => request(`/meetings${qs({ clientId })}`),
    get: (id) => request(`/meetings/${id}`),
    create: (fields) => writeRequest("/meetings", { method: "POST", body: JSON.stringify(fields) }),
    remove: (id) => writeRequest(`/meetings/${id}`, { method: "DELETE" }),
    addSection: (id, section) => writeRequest(`/meetings/${id}/sections`, { method: "POST", body: JSON.stringify({ section }) }),
    saveSection: (id, sectionId, rev, section) => writeRequest(`/meetings/${id}/sections/${sectionId}`, { method: "PUT", body: JSON.stringify({ rev, section }) }),
    removeSection: (id, sectionId) => writeRequest(`/meetings/${id}/sections/${sectionId}`, { method: "DELETE" }),
    reorder: (id, sectionIds) => writeRequest(`/meetings/${id}/order`, { method: "PUT", body: JSON.stringify({ sectionIds }) }),
    saveBlock: (id, block, rev, fields) => writeRequest(`/meetings/${id}/${block}`, { method: "PUT", body: JSON.stringify({ rev, block: fields }) }),
    setAgenda: (id, itemId, checked) => writeRequest(`/meetings/${id}/agenda`, { method: "PATCH", body: JSON.stringify({ itemId, checked }) }),
    setStatus: (id, to) => writeRequest(`/meetings/${id}/status`, { method: "POST", body: JSON.stringify({ to }) }),
    setGeneration: (id, notices) => writeRequest(`/meetings/${id}/generation`, { method: "POST", body: JSON.stringify({ notices }) }),
    recordSend: (id, send) => writeRequest(`/meetings/${id}/sends`, { method: "POST", body: JSON.stringify(send) }),
  },
  // Meeting templates (spec §3.6). list() → { items, defaults: { kickoff } }. update() sends the
  // template's _etag; a stale one returns 409 "Someone else saved this template…".
  meetingTemplates: {
    list: () => request("/meeting-templates"),
    create: (tmpl) => writeRequest("/meeting-templates", { method: "POST", body: JSON.stringify(tmpl) }),
    update: (id, tmpl, etag) => writeRequest(`/meeting-templates/${id}`, { method: "PUT", body: JSON.stringify({ ...tmpl, etag }) }),
    remove: (id) => writeRequest(`/meeting-templates/${id}`, { method: "DELETE" }),
  },
  users: {
    list: () => listItems("users"),
    save: (item) => upsertItem("users", item),
    remove: (id) => deleteItem("users", id),
  },
  goals: {
    list: () => fetchGoals(),
    save: (key, projectId, data) => saveGoal(key, projectId, data),
  },
  opsplanner: {
    list: () => listItems("opsplanner"),
    save: (item) => upsertItem("opsplanner", item),
    delete: (id) => deleteItem("opsplanner", id),
  },
  programs: {
    list: () => listItems("programs"),
    save: (item) => upsertItem("programs", item),
    delete: (id) => deleteItem("programs", id),
  },
  onePager: {
    list: () => request("/one-pager"),
    get: (id) => request(`/one-pager/${id}`),
    getVersion: (id, versionId) => request(`/one-pager/${id}/version/${versionId}`),
    save: (item) => request("/one-pager/item", { method: "PUT", body: JSON.stringify(item) }),
    generate: (id, variant, html) => request(`/one-pager/${id}/generate`, { method: "POST", body: JSON.stringify({ variant, html }) }),
    refine: (id, versionId, html, chatHistory) =>
      request(`/one-pager/${id}/refine`, { method: "POST", body: JSON.stringify({ versionId, html, chatHistory }) }),
    patchOverflow: (id, versionId, data) =>
      request(`/one-pager/${id}/version/${versionId}/overflow`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id) => request(`/one-pager/${id}`, { method: "DELETE" }),
  },
};

// Multipart upload for whitepaper extraction — bypasses the JSON Content-Type header
export async function extractOnePagerText(pagerId, file) {
  const form = new FormData();
  form.append("file", file);
  // No Content-Type here — the browser sets the multipart boundary. Only add auth.
  const headers = await authHeaders();
  const res = await fetch(`/api/one-pager/${pagerId}/extract-text`, { method: "POST", body: form, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Text extraction failed: ${res.status}`);
  }
  return res.json();
}
